import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Leaf, Flower2, HeartHandshake, LifeBuoy, ArrowLeft } from "lucide-react";
import { Seo } from "../components/Seo";
import { CatalogProductCard } from "../components/CatalogProductCard";
import { getContextShop } from "../lib/shopContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { piante: Leaf, fiori: Flower2, funebri: HeartHandshake };

export default function CatalogPage() {
  const [params] = useSearchParams();
  const ctx = getContextShop();
  const shopParam = params.get("shop") || (ctx && !ctx.is_fallback_store ? ctx.id : null);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/catalog-public`, { params: shopParam ? { shop_id: shopParam } : {} })
      .then(r => setData(r.data)).catch(() => setData({ shop: null, groups: [] }));
  }, [shopParam]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest" /></div>;

  const groups = data.groups.filter(g => g.products.length > 0);

  return (
    <div className="min-h-screen bg-white" data-testid="catalog-page">
      <Seo title="Catalogo completo — Flowerlive" description="Tutte le piante, i fiori, le composizioni e i prodotti funebri consegnati a domicilio in tutta Italia." path="/catalogo" />
      <section className="bg-cream border-b border-cream-dark py-14 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {shopParam && /^[a-f0-9]{24}$/.test(shopParam) && <Link to={`/shops/${shopParam}`} className="inline-flex items-center gap-1 text-sm text-sage hover:text-forest mb-4" data-testid="catalog-back-to-shop"><ArrowLeft className="h-4 w-4" />Torna alla vetrina</Link>}
          <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">Catalogo completo</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-forest mt-2 leading-tight">Le nostre creazioni floreali</h1>
          <p className="text-base md:text-lg text-sage mt-4 max-w-2xl">
            {data.shop ? <>Catalogo di <strong className="text-forest">{data.shop.name}</strong> · {data.total} articoli.{!data.shop.is_fallback_store && <> Solo le creazioni caricate da questo fiorista. <Link to="/catalogo?shop=catalog" className="underline text-sage" data-testid="catalog-general-link">Vedi il catalogo generale</Link></>}</> : "Nessun articolo disponibile."}
            {data.shop?.is_fallback_store && " Scegli il prodotto, poi inserisci l'indirizzo: se c'è un fiorista affiliato nella tua zona ti proponiamo lo stesso articolo preparato da lui."}
          </p>
          {data.shop?.is_fallback_store && (
            <p className="mt-3 inline-flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="catalog-fallback-note">
              <LifeBuoy className="h-3.5 w-3.5" />{data.shop.name} è il servizio di copertura Flowerlive, non un negozio fisico
            </p>
          )}
          {groups.length > 1 && (
            <nav className="flex flex-wrap gap-2 mt-8" data-testid="catalog-group-nav">
              {groups.map(g => (
                <a key={g.key} href={`#${g.key}`} className="px-4 py-2 rounded-full bg-white border border-cream-dark text-sm text-forest hover:border-brand hover:text-brand transition-colors" data-testid={`catalog-nav-${g.key}`}>
                  {g.label} <span className="text-sage">({g.products.length})</span>
                </a>
              ))}
            </nav>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-20">
        {groups.map(g => {
          const Icon = ICONS[g.key] || Flower2;
          return (
            <section key={g.key} id={g.key} className="scroll-mt-28" data-testid={`catalog-group-${g.key}`}>
              <div className="flex items-end justify-between gap-4 mb-8 border-b border-cream-dark pb-4">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-brand/10 flex items-center justify-center"><Icon className="h-5 w-5 text-brand" /></span>
                  <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-forest">{g.label}</h2>
                </div>
                <span className="text-sm text-sage">{g.products.length} articoli</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-6">
                {g.products.map(p => <CatalogProductCard key={p.id} product={p} />)}
              </div>
            </section>
          );
        })}
        {groups.length === 0 && <p className="text-center text-sage py-20" data-testid="catalog-empty">Il catalogo è in allestimento: torna presto.</p>}
      </div>
    </div>
  );
}
